import React, { useMemo, useState } from "react";

import {
  FormContext,
  useForm,
} from "react-hook-form/dist/react-hook-form.ie11";
import { cloneDeep, head } from "lodash";

import {
  DiscardPromptModal,
  UnsavedChangesPromptModal,
} from "@hubcapp/components";
import {
  FranchiseType,
  LessonTypePreference,
  PropositionAllocation,
  TravelTime,
  UserType,
  WorkingHour,
} from "@hubcapp/types";
import {
  isDormantFranchise,
  sortPropositionAllocationsForTabs,
} from "@hubcapp/utils";
import { useAuth, useTealium } from "@hubcapp/context";

import {
  InstructorPreferences,
  PreferencesFormFields,
  PreferencesStateProviderProps,
  PreferencesTab,
} from "./types";
import {
  addPlaceholderDaysAndSort,
  gatewayHoursToUiHours,
  uiHoursToGatewayHours,
} from "./workingHourTransforms";
import { buildTabItems, getPropositionCapacity } from "./helpers";
import {
  useMutationUpdatePreferences,
  useQueryPreferences,
  useQueryTestCentres,
} from "./hooks";
import FeatureNotAvailable from "../FeatureNotAvailable";
import Preferences from "./Preferences";
import validationSchema from "./validationSchema";

const StateProvider: React.FC<PreferencesStateProviderProps> = ({
  initialFormFields,
}) => {
  const { instructor } = useAuth();
  if (instructor.status !== "Active") {
    return <FeatureNotAvailable />;
  }

  const [cacheData, setCacheData] = useState<InstructorPreferences>();
  const [isDiscarding, setIsDiscarding] = useState(false);

  const [
    selectedPropositionAllocation,
    setSelectedPropositionAllocation,
  ] = useState<PropositionAllocation>();

  const form = useForm<PreferencesFormFields>({
    defaultValues: initialFormFields,
    mode: "onBlur",
    reValidateMode: "onChange",
    validationSchema: validationSchema,
  });

  const {
    formState: { isValid, dirty },
    handleSubmit: submitWrapper,
  } = form;

  const { trackEvent } = useTealium();

  const setFormValues = (
    values: InstructorPreferences,
    selectedPropositionAllocationId: string,
  ) => {
    const workingHours = addPlaceholderDaysAndSort(
      gatewayHoursToUiHours(
        values.workingHours.filter(
          (workingHours: WorkingHour) =>
            workingHours.lineItemKey === selectedPropositionAllocationId,
        ),
      ),
    );

    const filteredTravelTime = values.travelTime.find(
      (travelTime: TravelTime) =>
        travelTime.lineItemKey === selectedPropositionAllocationId,
    )?.time;

    const filteredLessonTypes = values.lessonTypes.filter(
      (lTPref: LessonTypePreference) =>
        lTPref.propositionAllocationId === selectedPropositionAllocationId,
    );

    form.reset(
      {
        lessonTypes: filteredLessonTypes,
        workingHours,
        travelTime: parseInt(filteredTravelTime || "15"),
        learnerCapacity: getPropositionCapacity(
          values.learnerCapacity,
          selectedPropositionAllocationId,
        ),
        capLock: Boolean(
          values.learnerCapacity.filter(capacity => capacity.locked).length,
        ),
        testCentres:
          values.testCentres?.filter(
            value => value.lineItemKey === selectedPropositionAllocationId,
          ) ?? [],
      },
      {
        dirty: false,
      },
    );

    form.register({ name: "travelTime" });
    form.register({ name: "learnerCapacity" });
    form.register({ name: "workingHours" });
    form.register({ name: "lessonTypes" });
    form.register({ name: "capLock" });
    form.register({ name: "testCentres" });
  };

  const onQueryCompleted = (data: { instructor?: InstructorPreferences }) => {
    if (data?.instructor) {
      setCacheData(cloneDeep(data.instructor));

      const sortedPropositions = sortPropositionAllocationsForTabs(
        data.instructor?.propositions || [],
      ).filter(
        propositionAllocation =>
          propositionAllocation.proposition.type !== FranchiseType.ThirdParty,
      );

      const initialFranchise = head(sortedPropositions);

      if (initialFranchise) {
        setSelectedPropositionAllocation(initialFranchise);
        setFormValues(data.instructor, initialFranchise.id || "");
      }
    }
  };

  const {
    data: testCentres,
    loading: loadingTestCentres,
  } = useQueryTestCentres();
  const testCentresList = testCentres?.testCentres || [];

  const { data, loading: queryLoading, refetch } = useQueryPreferences({
    onCompleted: onQueryCompleted,
    variables: {
      instructorId: instructor.id,
    },
    fetchPolicy: "no-cache",
  });

  const onMutationCompleted = (data: {
    updateInstructorPreferences?: InstructorPreferences;
  }) => {
    if (data && data.updateInstructorPreferences) {
      setCacheData(cloneDeep(data.updateInstructorPreferences));
      setFormValues(
        data.updateInstructorPreferences,
        selectedPropositionAllocation?.id || "",
      );
      refetch();
    }
  };

  const [
    updatePreferences,
    { loading: updateLoading },
  ] = useMutationUpdatePreferences({ onCompleted: onMutationCompleted });

  const { travelTime } = form.watch(["travelTime"]);

  const currentPropositionTravelTime = data?.instructor?.travelTime.find(
    (travelTime: TravelTime) =>
      travelTime?.lineItemKey === selectedPropositionAllocation?.id,
  );
  const currentTravelTime = currentPropositionTravelTime?.time || 0;

  const handleDiscard = () => {
    trackEvent({
      eventCategory: "Preferences",
      eventAction: "Button Click",
      eventLabel: "Yes Discard",
    });
    setIsDiscarding(false);
    cacheData &&
      setFormValues(
        cloneDeep(cacheData),
        selectedPropositionAllocation?.id || "",
      );
  };

  const handleSubmit = (data?: PreferencesFormFields) => {
    trackEvent({
      eventCategory: "Preferences",
      eventAction: "Button Click",
      eventLabel: "Submit",
    });
    if (!dirty && currentTravelTime !== travelTime) {
      return;
    }

    const learnerCapacity = data?.learnerCapacity?.maximum || 0;
    const lessonTypesPreferences = data?.lessonTypes?.map(ltp => ({
      lessonType: ltp.lessonType,
      enabled: ltp.enabled,
    }));

    const testCentres: string[] = data?.testCentres?.map(tc => tc.id) ?? [];

    const updateMutationInput = {
      propositionAllocationId: selectedPropositionAllocation?.id,
      instructorId: instructor.id,
      learnerCapacity: learnerCapacity,
      travelTime: data?.travelTime,
      workingHours: uiHoursToGatewayHours(
        data?.workingHours as Record<string, { from: string; to: string }>,
      ),
      lessonTypes: lessonTypesPreferences,
      userType: UserType.Instructor,
      testCentres,
    };

    updatePreferences({
      variables: updateMutationInput,
    });
  };

  const handleDiscardClick = () => {
    trackEvent({
      eventCategory: "Preferences",
      eventAction: "Button Click",
      eventLabel: "Discard",
    });
    if (!dirty) return;
    setIsDiscarding(true);
  };

  const handleChangeTab = (value: string) => {
    const foundItem = cacheData?.propositions?.find(
      (propositionAllocation: PropositionAllocation) =>
        propositionAllocation.id === value,
    );

    if (foundItem && cacheData) {
      setSelectedPropositionAllocation(foundItem);
      setFormValues(cacheData, foundItem.id);
    }
  };

  const handleDiscardCancel = () => {
    trackEvent({
      eventCategory: "Preferences",
      eventAction: "Button Click",
      eventLabel: "Cancel Discard",
    });
    setIsDiscarding(false);
  };

  const tabs: PreferencesTab[] = useMemo(() => {
    if (queryLoading) {
      // Dummy tab so the loading skeleton occupies the same space in the UI.
      const skeletonTab: PreferencesTab = {
        "aria-label": "",
        "data-test": "",
        key: "",
        label: "",
        value: "",
      };

      return [skeletonTab];
    }

    return buildTabItems(
      cacheData?.propositions?.filter(
        ({ proposition }) =>
          !isDormantFranchise(proposition) &&
          proposition.type !== FranchiseType.ThirdParty,
      ) || [],
    );
  }, [queryLoading, cacheData?.propositions]);

  return (
    <FormContext {...form}>
      <UnsavedChangesPromptModal
        showPrompt={dirty}
        onSaveClick={submitWrapper(handleSubmit)}
      />
      {isDiscarding && (
        <DiscardPromptModal
          onCancelClick={handleDiscardCancel}
          onConfirmClick={handleDiscard}
        />
      )}

      {selectedPropositionAllocation?.proposition?.type && (
        <Preferences
          loading={queryLoading || loadingTestCentres}
          updateLoading={updateLoading}
          tabs={tabs}
          franchiseType={selectedPropositionAllocation?.proposition?.type}
          selectedTab={selectedPropositionAllocation?.id || ""}
          onSubmit={submitWrapper(handleSubmit)}
          onDiscard={handleDiscardClick}
          onSelectTab={handleChangeTab}
          isFormValid={isValid}
          currentTravelTime={currentTravelTime}
          testCentres={testCentresList}
        />
      )}
    </FormContext>
  );
};

export default StateProvider;
